import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
	Box,
	Title,
	TextInput,
	Textarea,
	Button,
	Group,
	Text,
	Anchor,
} from '@mantine/core'
import {
	getCheckInById,
	updateCheckIn,
	createCheckIn,
} from '../services/actions/checkInActions'
import {
	getStationOptions,
	addStationOption,
} from '../services/actions/experienceActions'
import LookupSelect from '../components/LookupSelect'
import PurchasedItemsModal from '../components/PurchasedItemsModal'
import WarningsModal from '../components/WarningsModal'

const EditCheckInPage = () => {
	const { id, experienceSlug } = useParams()
	const isCreating = id === 'new'
	const navigate = useNavigate()

	const currentUser = useSelector((state) => state.user)
	const [checkIn, setCheckIn] = useState(null)
	const [error, setError] = useState('')
	const [saving, setSaving] = useState(false)
	const [showItemModal, setShowItemModal] = useState(false)
	const [showWarningModal, setShowWarningModal] = useState(false)
	const [editingWarning, setEditingWarning] = useState(null)
	const [showExtras, setShowExtras] = useState(false)
	const [defaultStation, setDefaultStation] = useState(null)

	useEffect(() => {
		getStationOptions(experienceSlug)
			.then((options) => {
				if (options.length > 0) {
					setDefaultStation(options[0])
				}
			})
			.catch((err) => console.error('Failed to fetch station options:', err))
	}, [experienceSlug])

	useEffect(() => {
		if (isCreating) {
			setCheckIn({
				travelerName: '',
				travelerEmail: '',
				travelerPhone: '',
				accountName: '',
				station: defaultStation?.id || '',
				stationName: defaultStation?.name || '',
				bookingNumber: '',
				notes: '',
				scheduledCheckInDate: new Date().toISOString().slice(0, 10),
				purchasedItems: [],
				warnings: [],
				checkInStatus: false,
			})
			return
		}

		getCheckInById(id)
			.then((data) => setCheckIn(data))
			.catch((err) => {
				console.error('Failed to fetch check-in:', err)
				setError('Failed to load check-in')
			})
	}, [id, isCreating, defaultStation])

	const handleSave = async () => {
		if (!checkIn) return

		const { travelerName, travelerEmail, travelerPhone } = checkIn

		if (!travelerName || !travelerEmail || !travelerPhone) {
			alert(
				'Please fill in all required fields: Traveler Name, Traveler Email, Traveler Phone.'
			)
			return
		}

		const payload = {
			...checkIn,
			scheduledCheckInDate: checkIn.scheduledCheckInDate
				? new Date(checkIn.scheduledCheckInDate)
				: new Date(),
			purchasedItems: (checkIn.purchasedItems || []).map((item) => ({
				id: item.id,
				issued: item.issued ?? false,
				paymentMethod: item.paymentMethod || '',
				paymentAmount: item.paymentAmount || '',
				addedByUser:
					item.paymentMethod && item.paymentMethod !== 'None'
						? currentUser?.authUser?.id || ''
						: '',
			})),
		}

		setSaving(true)
		try {
			if (isCreating) {
				const newCheckIn = await createCheckIn(experienceSlug, payload)
				navigate(`/experiences/${experienceSlug}/checkIn/${newCheckIn?.ids[0]}`)
			} else {
				await updateCheckIn(id, payload)
				navigate(`/experiences/${experienceSlug}/manage/checkins`)
			}
		} catch (err) {
			console.error('Failed to save check-in:', err)
			setError('Failed to save changes')
		} finally {
			setSaving(false)
		}
	}

	const handleChange = (field, value) => {
		setCheckIn((prev) => ({ ...prev, [field]: value }))
	}

	const fetchStationOptions = async (input) => {
		const results = await getStationOptions(experienceSlug)
		if (!input) return results
		return results.filter((opt) =>
			opt.name.toLowerCase().includes(input.toLowerCase())
		)
	}

	const createStationOption = async (label) => {
		const newOption = await addStationOption(experienceSlug, label)
		return newOption
	}

	const handleAddWarning = () => {
		setEditingWarning(null)
		setShowWarningModal(true)
	}

	const handleSaveWarning = (warningsArray) => {
		setCheckIn((prev) => ({
			...prev,
			warnings: warningsArray,
		}))
	}

	if (error) return <Text color='red'>{error}</Text>
	if (!checkIn) return <Text>Loading...</Text>

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='md'>
				{isCreating ? 'Add Check-In' : 'Edit Check-In'}
			</Title>

			<TextInput
				label='Traveler Name'
				required
				value={checkIn.travelerName}
				onChange={(e) => handleChange('travelerName', e.target.value)}
				mb='sm'
			/>

			<TextInput
				label='Traveler Email'
				required
				value={checkIn.travelerEmail}
				onChange={(e) => handleChange('travelerEmail', e.target.value)}
				mb='sm'
			/>

			<TextInput
				label='Traveler Phone'
				required
				value={checkIn.travelerPhone}
				onChange={(e) => handleChange('travelerPhone', e.target.value)}
				mb='sm'
			/>

			<Box
				mt='xl'
				px='md'
				py='md'
				style={{
					backgroundColor: '#f8f9fa',
					borderRadius: '8px',
					border: '1px solid #dee2e6',
				}}
			>
				<Group position='apart' mb='sm'>
					<Title order={4} m={0}>
						Purchased Items
					</Title>
					<Button
						variant='subtle'
						size='xs'
						onClick={() => setShowItemModal(true)}
					>
						Add / Edit
					</Button>
				</Group>

				{checkIn.purchasedItems?.length > 0 ? (
					<ul style={{ margin: 0, paddingLeft: '1rem' }}>
						{checkIn.purchasedItems.map((item) => (
							<li key={item.id}>
								<Text size='sm'>
									{item.name}{' '}
									{item.paymentMethod && item.paymentMethod !== 'None'
										? `(Paid $${item.paymentAmount || 0} via ${
												item.paymentMethod
										  })`
										: ''}
								</Text>
							</li>
						))}
					</ul>
				) : (
					<Text size='sm' color='dimmed'>
						None selected
					</Text>
				)}
			</Box>

			<Anchor
				mt='md'
				size='sm'
				onClick={() => setShowExtras((prev) => !prev)}
				style={{ display: 'inline-block', cursor: 'pointer' }}
			>
				{showExtras ? 'Hide Additional Fields' : 'Additional Fields'}
			</Anchor>

			{showExtras && (
				<>
					<TextInput
						label='Account Name'
						value={checkIn.accountName}
						onChange={(e) => handleChange('accountName', e.target.value)}
						mb='sm'
					/>

					<TextInput
						label='Check-in Date'
						type='date'
						value={checkIn.scheduledCheckInDate?.slice(0, 10) || ''}
						onChange={(e) =>
							handleChange('scheduledCheckInDate', e.target.value)
						}
						mb='sm'
					/>

					<LookupSelect
						label='Station'
						value={
							checkIn.station
								? { id: checkIn.station, name: checkIn.stationName }
								: null
						}
						onChange={(opt) => {
							handleChange('station', opt ? opt.id : '')
							handleChange('stationName', opt ? opt.name : '')
						}}
						fetchOptions={fetchStationOptions}
						onCreateNew={createStationOption}
						getOptionLabel={(opt) => opt.name}
						getOptionValue={(opt) => opt.id}
						placeholder='Select or create a station'
					/>

					<TextInput
						label='Booking Number'
						value={checkIn.bookingNumber}
						onChange={(e) => handleChange('bookingNumber', e.target.value)}
						mb='sm'
					/>

					<Textarea
						label='Notes'
						value={checkIn.notes}
						onChange={(e) => handleChange('notes', e.target.value)}
						autosize
						minRows={2}
						mb='md'
					/>

					<Box
						mt='md'
						px='md'
						py='md'
						style={{
							backgroundColor: '#f8f9fa',
							borderRadius: '8px',
							border: '1px solid #dee2e6',
						}}
					>
						<Group position='apart' mb='sm'>
							<Title order={4} m={0}>
								Warnings
							</Title>
							<Button variant='subtle' size='xs' onClick={handleAddWarning}>
								Add / Edit
							</Button>
						</Group>

						{checkIn.warnings?.length > 0 ? (
							<ul style={{ margin: 0, paddingLeft: '1rem' }}>
								{checkIn.warnings.map((w, i) => (
									<li key={i}>
										<Text size='sm'>
											<strong>{w.level}</strong>: {w.title}
										</Text>
									</li>
								))}
							</ul>
						) : (
							<Text size='sm' color='dimmed'>
								No warnings
							</Text>
						)}
					</Box>
				</>
			)}

			<Group mt='xl'>
				<Button onClick={handleSave} loading={saving}>
					{isCreating ? 'Create Check-In' : 'Save Changes'}
				</Button>
				<Button
					variant='light'
					color='gray'
					onClick={() =>
						navigate(`/experiences/${experienceSlug}/manage/checkins`)
					}
				>
					Cancel
				</Button>
			</Group>

			<WarningsModal
				opened={showWarningModal}
				onClose={() => setShowWarningModal(false)}
				initial={checkIn.warnings}
				onSave={handleSaveWarning}
			/>

			<PurchasedItemsModal
				show={showItemModal}
				onClose={() => setShowItemModal(false)}
				experienceSlug={experienceSlug}
				currentItems={checkIn.purchasedItems}
				currentUserId={currentUser?.authUser?.id}
				onSave={(newItems) =>
					setCheckIn((prev) => {
						const prevMap = Object.fromEntries(
							(prev.purchasedItems || []).map((item) => [item.id, item])
						)
						const merged = newItems.map((item) => ({
							...item,
							issued: prevMap[item.id]?.issued ?? false,
						}))
						return { ...prev, purchasedItems: merged }
					})
				}
			/>
		</Box>
	)
}

export default EditCheckInPage
